import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';
import { Order } from '../../types';
import './Customer.css';

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const data = await authAPI.getOrderHistory();
        setOrders(data.slice(0, 5)); // Get latest 5 orders
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const getStatusBadgeClass = (status?: string) => {
    switch (status) {
      case 'delivered':
        return 'status-badge status-delivered';
      case 'shipped':
        return 'status-badge status-shipped';
      case 'processing':
        return 'status-badge status-processing';
      case 'cancelled':
        return 'status-badge status-cancelled';
      default:
        return 'status-badge status-pending';
    }
  };

  const pendingOrders = orders.filter(o => o.status === 'pending' || o.status === 'processing');

  return (
    <div className="customer-container">
      <div className="customer-content">
        <div className="dashboard-header">
          <h1 className="customer-title">
            {t('customer.welcome') || 'স্বাগতম'}, {user?.full_name || user?.username}!
          </h1>
          <p className="dashboard-subtitle">
            {t('customer.dashboard_subtitle') || 'আপনার অ্যাকাউন্ট ড্যাশবোর্ড'}
          </p>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📦</div>
            <div className="stat-content">
              <h3>{orders.length}</h3>
              <p>{t('customer.total_orders') || 'মোট অর্ডার'}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">⏳</div>
            <div className="stat-content">
              <h3>{pendingOrders.length}</h3>
              <p>{t('customer.pending_orders') || 'প্রসেসিং অর্ডার'}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">👤</div>
            <div className="stat-content">
              <h3>{profile?.city || 'N/A'}</h3>
              <p>{t('customer.location') || 'অবস্থান'}</p>
            </div>
          </div>
        </div>

        <div className="quick-links">
          <h2>{t('customer.quick_links') || 'দ্রুত লিংক'}</h2>
          <div className="links-grid">
            <Link to="/profile" className="quick-link-card">
              <span className="link-icon">👤</span>
              <span className="link-text">{t('customer.my_profile') || 'আমার প্রোফাইল'}</span>
            </Link>
            <Link to="/order-history" className="quick-link-card">
              <span className="link-icon">📋</span>
              <span className="link-text">{t('customer.order_history') || 'অর্ডার ইতিহাস'}</span>
            </Link>
            <Link to="/products" className="quick-link-card">
              <span className="link-icon">🛍️</span>
              <span className="link-text">{t('nav.products') || 'পণ্য'}</span>
            </Link>
          </div>
        </div>

        <div className="recent-orders">
          <div className="section-header">
            <h2>{t('customer.recent_orders') || 'সাম্প্রতিক অর্ডার'}</h2>
            <Link to="/order-history" className="view-all-link">
              {t('customer.view_all') || 'সব দেখুন'} →
            </Link>
          </div>

          {loading ? (
            <div className="loading-state">Loading orders...</div>
          ) : orders.length === 0 ? (
            <div className="empty-state">
              <p>{t('customer.no_orders') || 'কোন অর্ডার নেই'}</p>
              <Link to="/products" className="shop-now-button">
                {t('customer.start_shopping') || 'কেনাকাটা শুরু করুন'}
              </Link>
            </div>
          ) : (
            <div className="orders-table">
              <table>
                <thead>
                  <tr>
                    <th>{t('customer.order_number') || 'অর্ডার নম্বর'}</th>
                    <th>{t('customer.date') || 'তারিখ'}</th>
                    <th>{t('customer.total') || 'মোট'}</th>
                    <th>{t('customer.status') || 'স্ট্যাটাস'}</th>
                    <th>{t('customer.action') || 'অ্যাকশন'}</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id}>
                      <td>#{order.order_number}</td>
                      <td>{new Date(order.created_at!).toLocaleDateString()}</td>
                      <td>৳{order.final_amount?.toFixed(2)}</td>
                      <td>
                        <span className={getStatusBadgeClass(order.status)}>
                          {order.status_display || order.status}
                        </span>
                      </td>
                      <td>
                        <Link to={`/track-order?order=${order.order_number}`} className="track-link">
                          {t('customer.track') || 'ট্র্যাক করুন'}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
